import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import type { BlueprintMember } from './default-structure';

/**
 * Employee model — mirrors backend/app/schemas.py::EmployeeOut. Fields the UI
 * doesn't render (e.g. created_at / updated_at) are still present for
 * server round-trip on edit. Will be replaced by OpenAPI codegen later.
 */
export type EmploymentType =
  | 'FULL_TIME' | 'PART_TIME' | 'CONTRACT' | 'CONSULTANT' | 'INTERN';

export type EmployeeStatus = 'ACTIVE' | 'ON_LEAVE' | 'RESIGNED' | 'TERMINATED';

export type SalaryOverride = {
  /**
   * ADDITIONAL — a brand-new pay line not on the structure (per-employee add).
   * OVERRIDE   — replaces the structure-resolved amount for a matching pay item.
   * REMOVE     — suppresses a structure/template pay item entirely for this
   *              employee (the line disappears from the breakdown instead of
   *              being zeroed). `amount` is ignored for REMOVE.
   */
  type: 'ADDITIONAL' | 'OVERRIDE' | 'REMOVE';
  pay_item_id?: string;
  short_code?: string;
  name: string;
  amount: number;
  formula_type?: string;
  /**
   * The percentage behind `amount` when HR edited the row via the "%" column
   * (paired with formula_type = PERCENT_OF_BASIC / _GROSS / _CTC).
   *
   * `amount` is ALWAYS sent alongside and stays authoritative — the payroll
   * engine reads only the rupee figure, so this field can never change what is
   * paid. It exists so re-opening the editor shows "50 %" again instead of
   * back-deriving it from two rounded rupee amounts.
   */
  formula_value?: number | null;
  /**
   * The Excel-style expression behind `amount` when HR edited the row via the
   * "Expression" basis (paired with formula_type = FORMULA). Like formula_value,
   * `amount` is sent alongside and stays authoritative — the engine reads the
   * rupee figure — so this is re-display metadata: re-opening the editor shows
   * the formula again instead of a bare number. The backend also resolves it
   * server-side (see `_enrich_override_rows`) when no amount is provided.
   */
  formula_expr?: string;
  note?: string;
};

export type Employee = {
  id: string;
  emp_id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  mobile: string | null;
  entity_id: string;
  department: string | null;
  designation: string | null;
  cost_center: string | null;
  location: string | null;
  work_state: string | null;
  /** LWF state code. Blank → LWF falls back to the PT state. */
  lwf_state: string | null;
  gender: 'M' | 'F' | 'OTHER' | null;
  skill_type: 'SKILLED' | 'SEMI_SKILLED' | 'UNSKILLED' | null;
  work_location_id: string | null;
  nps_pct: number | string | null;
  superannuation_pct: number | string | null;
  vpf_pct: number | string | null;
  /** EE-share PF rate override (%). null = statutory 12%. Employer total stays 12%. */
  pf_rate_employee_pct: number | string | null;
  pf_ceiling_mode: 'AMOUNT' | 'PERCENT' | 'UNCAPPED' | null;
  pf_ceiling_amount: number | string | null;
  pf_ceiling_pct: number | string | null;
  /** Per-employee OVERRIDE for LOP PF-ceiling behaviour (null = inherit the
   *  entity/system default = FIXED):
   *    FIXED   — ceiling stays at full ₹25k; only wages are prorated (default,
   *              standard EPFO practice).
   *    PRORATE — ceiling × paid_days/cal_days. */
  pf_absent_behavior: 'PRORATE' | 'FIXED' | null;
  /** Form 11 opt-out (para 26A): >Rs 25k Basic+DA at first join → no PF deducted. */
  pf_voluntary_excluded: boolean;
  /** First PF membership date — for the post-Sep-2014 EPS-ineligibility rule. */
  pf_join_date: string | null;
  /** Basic+DA at first PF join — paired with pf_join_date for EPS eligibility. */
  pf_wage_at_join: number | string | null;
  /** International Worker: no PF wage ceiling, no Rs 1,250 EPS cap. */
  is_international_worker: boolean;
  /** Apprentices Act: exempt from both PF and ESIC. */
  is_apprentice: boolean;
  /** Person with Disability: ESIC gross ceiling rises from Rs 21k to Rs 25k. */
  is_pwd: boolean;
  manager_emp_id: string | null;
  employment_type: EmploymentType;
  date_of_joining: string | null;
  date_of_leaving: string | null;
  date_of_birth: string | null;
  pan: string | null;
  pf_uan: string | null;
  esic_ip_no: string | null;
  aadhaar_last4: string | null;
  nps_pran: string | null;
  city_tier: 'METRO' | 'NON_METRO' | null;
  bank_account_no: string | null;
  bank_ifsc: string | null;
  bank_name: string | null;
  salary_structure_id: string | null;
  annual_ctc: number | string | null;
  salary_overrides: SalaryOverride[];
  pay_group_id: string | null;
  // ── Sweep KK additions (25 fields) ──────────────────────────────────
  // Family / identity (5)
  father_or_husband_name?: string | null;
  mother_name?: string | null;
  spouse_name?: string | null;
  marital_status?: 'SINGLE' | 'MARRIED' | 'DIVORCED' | 'WIDOWED' | null;
  /** Flexible list of dependents — drives 80D parents detection,
   *  gratuity nomination, FBP family allocations. */
  dependents?: Array<{
    name: string;
    relation: string;
    dob?: string | null;
    is_dependent_for_80d?: boolean;
  }>;
  // Address (2)
  current_address?: string | null;
  permanent_address?: string | null;
  // KYC + demographics (6)
  aadhaar_full?: string | null;
  nationality?: string | null;
  passport_no?: string | null;
  blood_group?: string | null;
  qualification?: string | null;
  religion?: string | null;
  // HR lifecycle (4)
  grade?: string | null;
  probation_end_date?: string | null;
  confirmation_date?: string | null;
  notice_period_days?: number | null;   // null = inherit entity FnF policy
  // Emergency contact (3)
  emergency_contact_name?: string | null;
  emergency_contact_relation?: string | null;
  emergency_contact_phone?: string | null;
  // Banking (2)
  bank_branch?: string | null;
  bank_account_type?: 'SAVINGS' | 'CURRENT' | null;
  // PF (2)
  pf_member_no?: string | null;
  pf_nomination?: Array<{ name: string; relation: string; share_pct: number }>;
  // Compensation lifecycle (1)
  ctc_effective_date?: string | null;
  status: EmployeeStatus;
  created_at?: string;
  updated_at?: string;
};

/** Payload for create/update — same shape as EmployeeIn on the backend. */
export type EmployeeWritable = Omit<Employee, 'id' | 'created_at' | 'updated_at'>;

export type EmployeeFilters = {
  entity_id?: string;
  status?: string;
  department?: string;
  employment_type?: string;
  search?: string;
  page?: number;
  page_size?: number;
};

// ── List + count ─────────────────────────────────────────────────────────────

export function useEmployees(
  filters: EmployeeFilters,
  opts: { enabled?: boolean; keepPreviousPage?: boolean } = {},
) {
  return useQuery({
    queryKey: ['employees', filters],
    queryFn: () => api.get<Employee[]>('/employees', filters as Record<string, string | number | boolean | undefined>),
    enabled: opts.enabled !== false,
    // Paged callers keep the current rows on screen while the next page loads.
    placeholderData: opts.keepPreviousPage ? (prev) => prev : undefined,
  });
}

export function useEmployeeCount(
  filters: Omit<EmployeeFilters, 'page' | 'page_size'>,
  opts: { keepPrevious?: boolean } = {},
) {
  return useQuery({
    queryKey: ['employees', 'count', filters],
    queryFn: () => api.get<{ total: number }>('/employees/count', filters as Record<string, string | number | boolean | undefined>),
    // A filtered list (search as you type) keeps the last total on screen
    // while the new one loads, instead of dropping back to "loading".
    placeholderData: opts.keepPrevious ? (prev) => prev : undefined,
  });
}

export function useEmployeeStats(entity_id?: string) {
  return useQuery({
    queryKey: ['employees', 'stats', entity_id ?? null],
    queryFn: () => api.get<Record<string, number>>('/employees/stats', entity_id ? { entity_id } : undefined),
  });
}

// ── Salary source ───────────────────────────────────────────────────────────

/**
 * What an employee is paid from — derived on the server from the salary
 * structure and the employee's audit trail (never stored, never read by
 * payroll). See `api/employees._classify_salary_source`.
 *
 *   STRUCTURE — a salary structure is assigned; `label` is its name
 *   IMPORTED  — paid from pay items a bulk import (Import Salary) wrote
 *   MANUAL    — paid from pay items HR entered on a salary screen
 *   NONE      — nothing configured yet
 */
export type SalarySourceKind = 'STRUCTURE' | 'IMPORTED' | 'MANUAL' | 'NONE';

export type SalarySource = {
  kind: SalarySourceKind;
  /** Display text: the structure name, "Imported pay items", "Direct pay items". */
  label: string | null;
  /** The import that produced the current pay items (IMPORTED only). */
  imported_at: string | null;
  imported_from: string | null;
  imported_by: string | null;
  /** Latest hand edit of those pay items, if any. */
  edited_at: string | null;
  /** True when the import pre-dates import auditing, so file/date are unknown. */
  inferred: boolean;
};

// ── Batch monthly salary breakdown (list columns) ──────────────────────────

/** Per-employee monthly breakdown returned by POST /employees/salary-breakdowns.
 *  All amounts are monthly. Zeros when the employee has no structure/CTC. */
export type SalaryBreakdown = {
  basic: number;
  hra: number;
  conveyance: number;
  special: number;
  uniform: number;
  /** Every resolved head, keyed by its catalog code (upper-cased short_code),
   *  monthly. This is what the list's DYNAMIC salary columns read: the five
   *  named keys above exist only because the aggregates below are built from
   *  them, not because the column set is fixed. */
  heads: Record<string, number>;
  /** The EARNING heads of `heads`, split by where THIS employee's row counted
   *  them: `gross_heads` sums to `gross`, `off_gross_heads` to `off_gross`.
   *  The flag is per row (a structure component overrides its pay item), so
   *  the catalog's `in_gross` — which decides only which SIDE of Gross / mo a
   *  head's list column sits on — must never be used to split the money. */
  gross_heads?: Record<string, number>;
  off_gross_heads?: Record<string, number>;
  /** Earnings that count toward gross — excludes heads the pay-item catalog
   *  marks `is_in_gross=false`, matching what the payroll engine books. */
  gross: number;
  /** Earnings deliberately kept OUT of gross (`is_in_gross=false`). The list
   *  no longer has an Off-gross column — each such head is its own column
   *  after Gross / mo — but the total is still returned for other callers
   *  (the default-structure move preview). */
  off_gross: number;
  /** In-gross earnings with no named column of their own.
   *
   *  Computed on the SERVER from the components. It used to be derived here as
   *  `max(0, gross - (basic+hra+conveyance+special+uniform))`, which is only
   *  right while every named head is inside gross: a tenant that flags one of
   *  them `is_in_gross=false` (HRA, in the case that found this) subtracts
   *  money that was never added, the result goes negative, the clamp turns it
   *  into a confident 0 — and every unnamed head silently vanishes from the
   *  list. Never derive it in the browser again. The list no longer shows an
   *  "Other earn." column (every head has its own); still returned. */
  other_earn: number;
  /** Employee-side statutory deductions. Still returned; the Employees list
   *  no longer shows them (it shows employer cost only). */
  pf_ee: number;
  esic_ee: number;
  pt: number;
  lwf_ee: number;
  /** Voluntary PF from the employee's profile vpf_pct. */
  vpf_ee: number;
  /** Named employer heads, each matched the way the payroll engine matches it
   *  (alias family, short code or category — never the display group). */
  pf_er: number;
  esic_er: number;
  lwf_er: number;
  /** NPS / Superannuation (ER) from the employee's profile % — inside
   *  employer_total, named for the salary import preview. */
  nps_er: number;
  sann_er: number;
  /** Gratuity provision, whichever Salary-tab section its row renders in. */
  gratuity: number;
  /** Employer heads with no named column above (NPS, superannuation, EDLI,
   *  PF admin, custom heads), keyed by catalog code — one grid column each. */
  employer_heads?: Record<string, number>;
  /** Display name + kind of every resolved head, keyed by catalog code. Names
   *  heads the catalog cannot (profile-driven rows with no pay item). */
  head_meta?: Record<string, { name: string; item_type: string }>;
  /** EVERY employer contribution row: the named heads above (gratuity
   *  included) plus EDLI, PF admin, NPS, superannuation and custom heads —
   *  the Salary tab's Employer (in-CTC) + Employer (above CTC). */
  employer_total: number;
  /** CTC-reservation employer rows + variable/flexi pools. */
  ctc_reservations: number;
  monthly_ctc: number;
  /** Package the resolved breakdown COMMITS per month:
   *  in-CTC earnings + in-CTC employer contributions + variable/flexi pools.
   *  Same server-side definition the CTC over-commitment guard and the Salary
   *  tab's summary strip use, so the three can never disagree. */
  monthly_package: number;
  /** `monthly_package * 12`. For an employee with no agreed CTC on file — the
   *  normal state after a Pay Item Import, which writes pay items but never an
   *  `annual_ctc` — this is the only annual figure there is. */
  annual_package: number;
  /** True when no Annual CTC is assigned, so `annual_package` is derived
   *  rather than agreed. Label it in the UI; never show it as an agreed CTC. */
  ctc_is_derived: boolean;
  /** What the employee is paid from — says where a derived CTC came from. */
  salary_source?: SalarySource | null;
};

/**
 * Fetch monthly salary breakdowns for a set of employees (the visible page of
 * the list). Keyed by employee id. The list endpoint stays lean; this computes
 * each employee's structure on demand only for the rows on screen.
 */
export function useSalaryBreakdowns(ids: string[]) {
  const sorted = [...ids].sort();
  return useQuery({
    queryKey: ['employees', 'breakdowns', sorted],
    queryFn: () => api.post<Record<string, SalaryBreakdown>>('/employees/salary-breakdowns', { ids }),
    enabled: ids.length > 0,
    placeholderData: (prev) => prev,   // smooth page transitions
  });
}

// ── Configured pay heads (the list's dynamic salary columns) ───────────────

/** One head in the tenant's own catalog, as `GET /employees/salary-heads`
 *  returns it: ordered earnings, then employee deductions, then employer
 *  contributions, each block in its salary structures' own `sort_order`. */
export type SalaryHead = {
  /** Upper-cased short_code. The key `SalaryBreakdown.heads` uses. */
  code: string;
  name: string;
  item_type: 'EARNING' | 'DEDUCTION' | 'EMPLOYER_CONTRIBUTION';
  category: string | null;
  /** The pay item's own in-gross flag. A hint for the UI ("this head sits
   *  outside Gross"), never the figure — a structure component can override it
   *  per structure, and only the server-side amounts resolve that. */
  in_gross: boolean;
  /** A VARIABLE payout head (pay_category = VARIABLE, or non-recurring).
   *  Reserved inside CTC but released through the Variable Payout register, so
   *  the engine never pays it in a regular run and it is NEVER part of a
   *  month's gross — whatever `in_gross` says. */
  variable: boolean;
  display_group: string | null;
  sort_order: number;
  display_order: number;
  /** Employer heads only: true when the list already has a named column for
   *  it (PF / ESIC / LWF (ER), Gratuity); false → it gets a column of its own. */
  grid_column?: boolean;
};

/**
 * The pay heads the Employees list can show as columns. Cached for the session:
 * it changes only when someone edits Pay Items or a salary structure, and the
 * list re-reads it on a page reload.
 */
export function useSalaryHeads(entityId?: string) {
  return useQuery({
    queryKey: ['employees', 'salary-heads', entityId ?? ''],
    queryFn: () => api.get<{ heads: SalaryHead[] }>(
      `/employees/salary-heads${entityId ? `?entity_id=${entityId}` : ''}`,
    ),
    staleTime: 5 * 60_000,
  });
}

// ── Single CRUD ──────────────────────────────────────────────────────────────

export function useEmployee(id: string | null | undefined) {
  return useQuery({
    queryKey: ['employees', 'detail', id],
    queryFn: () => api.get<Employee>(`/employees/${id}`),
    enabled: !!id,
  });
}

export function useCreateEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: EmployeeWritable) => api.post<Employee>('/employees', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['employees'] });
    },
  });
}

export function useUpdateEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; body: EmployeeWritable }) =>
      api.put<Employee>(`/employees/${vars.id}`, vars.body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['employees'] });
    },
  });
}

/**
 * Sweep MM.6 #23 — Partial PATCH on /employees/{id}. The backend has
 * supported this since day one (full replace via PUT, partial via PATCH);
 * frontend had no hook so any field-level edit went through PUT and shipped
 * the entire employee body — increasing collision surface for concurrent
 * edits and burning bandwidth.
 *
 * The drawer keeps using PUT (it's editing the whole employee anyway). Use
 * this for targeted writes like "toggle status", "update notice_period",
 * or future row-inline edits on the List page.
 */
export function usePatchEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; patch: Partial<EmployeeWritable> }) =>
      api.patch<Employee>(`/employees/${vars.id}`, vars.patch),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['employees'] });
      qc.invalidateQueries({ queryKey: ['employees', vars.id] });
    },
  });
}

export function useDeleteEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/employees/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['employees'] });
    },
  });
}

// ── Bulk operations ──────────────────────────────────────────────────────────

export type BulkUpdateRequest = {
  employee_ids: string[];
  updates: Partial<Record<
    'department' | 'designation' | 'cost_center' | 'location' |
    'manager_emp_id' | 'employment_type' | 'status' |
    'salary_structure_id' | 'annual_ctc' | 'pay_group_id' |
    'work_location_id' | 'work_state' | 'lwf_state',
    string | number | null
  >>;
};

export type BulkUpdateResponse = {
  updated: number;
  skipped: { id: string; reason: string }[];
  fields: string[];
};

export function useBulkUpdateEmployees() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: BulkUpdateRequest) =>
      api.post<BulkUpdateResponse>('/employees/bulk-update', req),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['employees'] }),
  });
}

export type BulkImportError = { row: number; message: string };
export type BulkImportResult = {
  filename: string;
  total_rows: number;
  inserted_rows: number;
  updated_rows: number;
  error_rows: number;
  errors: BulkImportError[];
  /** Portal logins provisioned by an employee import. Importing employees is
   *  supposed to give them a Users-page account; these say whether it did.
   *  Absent (→ 0 / []) on imports that don't touch users. */
  logins_created?: number;
  logins_skipped?: number;
  /** Variable-head cells routed to an employee's Variable (above CTC)
   *  schedule instead of monthly pay. They carry the AMOUNT only — a pay grid
   *  has no month column — so each still needs a payout month picked. */
  variable_payouts?: number;
  /** Non-fatal advisories — nothing was skipped, but HR needs to know. */
  notes?: string[];
  /** Salary imports, dry run only: each changed employee's monthly split as
   *  it will be after the import, resolved by the Salary tab's own engine. */
  previews?: SalaryImportPreview[];
};

export type SalaryImportPreview = {
  emp_id: string;
  employee_name: string;
  structure: string;
  previous_annual_ctc: number | null;
  annual_ctc: number | null;
  basic: number;
  hra: number;
  special: number;
  gross: number;
  pf_ee: number;
  vpf_ee: number;
  pf_er: number;
  nps_er: number;
  sann_er: number;
  employer_total: number;
  /** Increment import only. */
  effective_from?: string;
  status?: 'APPLY NOW' | 'SCHEDULED';
  /** Back-dated increments: arrears for payroll months already finalised
   *  since effective_from, replayed at the new CTC (nothing written yet). */
  arrears_estimate?: number;
  arrears_periods?: string[];
  /** Where the arrears go ("added to the Sep 2026 run"), or why there are none. */
  arrears_note?: string;
};

/**
 * Bulk-import wrapper. The endpoint takes multipart/form-data with the CSV
 * file. We expose two modes:
 *   dryRun=true  → validate-only, returns the same result shape with no DB
 *                  writes. Used by the preview step.
 *   dryRun=false → commits the upload.
 */
export function useBulkImportEmployees() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { file: File; entityId: string; dryRun: boolean }) => {
      const fd = new FormData();
      fd.append('file', vars.file);
      // apiFetch lets us pass a FormData body; do not set Content-Type so the
      // browser includes the multipart boundary.
      const path = `/employees/upload?entity_id=${encodeURIComponent(vars.entityId)}&dry_run=${vars.dryRun ? 'true' : 'false'}`;
      const { apiFetch } = await import('@/lib/api-client');
      return apiFetch<BulkImportResult>(path, { method: 'POST', body: fd });
    },
    onSuccess: (_d, vars) => {
      // Only refresh when we actually committed
      if (!vars.dryRun) qc.invalidateQueries({ queryKey: ['employees'] });
    },
  });
}

/** Bulk-import per-employee pay-item overrides. Same shape as
 *  useBulkImportEmployees but hits a different endpoint and writes to
 *  Employee.salary_overrides (the JSONB list the calculator reads). */
export function useUploadPayOverrides() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { file: File; entityId: string; dryRun: boolean }) => {
      const fd = new FormData();
      fd.append('file', vars.file);
      const path = `/employees/upload-pay-overrides?entity_id=${encodeURIComponent(vars.entityId)}&dry_run=${vars.dryRun ? 'true' : 'false'}`;
      const { apiFetch } = await import('@/lib/api-client');
      return apiFetch<BulkImportResult>(path, { method: 'POST', body: fd });
    },
    onSuccess: (_d, vars) => {
      if (!vars.dryRun) qc.invalidateQueries({ queryKey: ['employees'] });
    },
  });
}

/** Bulk-import per-employee salary-structure assignments + CTC. */
/** Salary Structure Import popup — `variant` picks the tab: 'increment' posts
 *  the increment sheet, anything else the structure-assignment sheet. */
export function useUploadSalaryImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { file: File; entityId: string; dryRun: boolean; variant?: string }) => {
      const fd = new FormData();
      fd.append('file', vars.file);
      const endpoint = vars.variant === 'increment'
        ? '/employees/upload-increments' : '/employees/upload-structure-assignments';
      const path = `${endpoint}?entity_id=${encodeURIComponent(vars.entityId)}&dry_run=${vars.dryRun ? 'true' : 'false'}`;
      const { apiFetch } = await import('@/lib/api-client');
      return apiFetch<BulkImportResult>(path, { method: 'POST', body: fd });
    },
    onSuccess: (_d, vars) => {
      if (vars.dryRun) return;
      qc.invalidateQueries({ queryKey: ['employees'] });
      qc.invalidateQueries({ queryKey: ['employee'] });
      qc.invalidateQueries({ queryKey: ['employee-salary-assignment'] });
      qc.invalidateQueries({ queryKey: ['ctc-revisions'] });
    },
  });
}

// ── Pay Item Import: which columns the template offers ──────────────────────

export type PayOverrideColumns = {
  columns: { short_code: string; name: string; item_type: string }[];
  /** Subset of `columns` whose amounts are routed to the above-CTC payout
   *  schedule rather than written as monthly pay. Those need a payout month. */
  variable_columns: { short_code: string; name: string; reason: string }[];
  /** Variable heads that sit INSIDE CTC — they import like any other column
   *  (no month, no schedule) and the Salary tab groups them separately. */
  variable_in_ctc_columns: { short_code: string; name: string; reason: string }[];
  /** DEFAULT — `columns` are the default structure's heads only (the template
   *  the dialog's button downloads) · ALL — every pay item in the catalog. */
  scope: 'DEFAULT' | 'ALL';
  /** Columns in the full-catalog template (`scope=all`). */
  full_columns_count: number;
  /** The default salary structure employees with no structure are placed on
   *  after the import (null when there is none this entity may use). */
  default_structure: {
    id: string;
    name: string;
    /** The template column that prices it — its CTC. */
    ctc_column: string;
    /** Template columns its formulas re-price on placement. */
    priced_columns: BlueprintMember[];
    /** Template columns payroll computes on placement (PF, ESIC, gratuity …).
     *  Every column in neither list stays as typed. */
    computed_columns: BlueprintMember[];
  } | null;
};

/**
 * What the Pay Item Import template offers, and which of those columns are
 * routed to the Variable (above CTC) schedule. Surfaced in the import dialog
 * so a MIS-TAGGED head — a bonus still classified as an ordinary recurring
 * earning, which therefore imports as a permanent monthly amount — is obvious
 * before HR uploads rather than after payroll runs.
 */
export function usePayOverrideColumns(enabled = true, entityId: string | null = null) {
  return useQuery({
    queryKey: ['pay-override-columns', entityId],
    queryFn: () => api.get<PayOverrideColumns>('/employees/pay-overrides-columns',
      entityId ? { entity_id: entityId } : undefined),
    enabled,
    staleTime: 60_000,
  });
}

// ── Repair: portal logins ───────────────────────────────────────────────────

export type ProvisionLoginsResult = {
  employees: number;
  logins_created: number;
  logins_skipped: number;
  /** Logins whose username was pulled back onto the employee's current emp_id. */
  usernames_synced: number;
  /** Logins put back on the entity's default employee password. */
  passwords_reset: number;
  /** Employees holding more than one login — reported, never auto-merged. */
  duplicate_logins: number;
  notes: string[];
};

/**
 * Bring the entity's portal logins back in sync with its employees: create the
 * missing ones, and rename any whose username drifted from a changed emp_id.
 * Idempotent — for rosters imported before the import provisioned logins
 * reliably, so the Users page can be brought back in sync without re-uploading.
 *
 * `resetPasswords` additionally puts every employee login back on the entity's
 * default employee password. Opt-in, because it discards passwords employees
 * set themselves; the password is never sent from here — the server reads it
 * from the entity so it can't end up in a URL.
 */
export function useProvisionLogins() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { entityId: string; resetPasswords?: boolean }) => {
      const path = `/employees/provision-logins?entity_id=${encodeURIComponent(vars.entityId)}`
        + (vars.resetPasswords ? '&reset_passwords=true' : '');
      const { apiFetch } = await import('@/lib/api-client');
      return apiFetch<ProvisionLoginsResult>(path, { method: 'POST' });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

// ── Uploads history ─────────────────────────────────────────────────────────

export type EmployeeUploadRow = {
  id: string;
  filename: string;
  uploaded_by: string | null;
  total_rows: number;
  inserted_rows: number;
  updated_rows: number;
  error_rows: number;
  errors: { row?: number; emp_id?: string; error?: string }[] | null;
  uploaded_at: string;
};

export function useEmployeeUploads(entity_id: string | null | undefined) {
  return useQuery({
    queryKey: ['employees', 'uploads', entity_id],
    queryFn: () => api.get<EmployeeUploadRow[]>('/employees/uploads', { entity_id: entity_id! }),
    enabled: !!entity_id,
  });
}

// ── Audit log (profile + salary + arrears combined) ────────────────────────

export type EmployeeAuditEntry = {
  id: string;
  kind: 'PROFILE' | 'SALARY' | 'ARREARS' | string;
  // PROFILE
  change_type?: string;
  fields_changed?: string[];
  before?: Record<string, unknown>;
  after?: Record<string, unknown>;
  // SALARY
  structure_id?: string;
  structure_name?: string;
  annual_ctc?: number;
  // ARREARS
  effective_from?: string;
  effective_to?: string;
  total_arrears?: number;
  status?: string;
  applied_run_id?: string | null;
  // Common
  note?: string | null;
  change_note?: string | null;
  changed_by?: string | null;
  created_at: string;
};

export function useEmployeeAuditLog(employee_id: string | null | undefined) {
  return useQuery({
    queryKey: ['employees', 'audit', employee_id],
    queryFn: () => api.get<EmployeeAuditEntry[]>(`/employees/${employee_id}/audit-log`),
    enabled: !!employee_id,
  });
}

// ── Salary assignment (current breakdown) ──────────────────────────────────

export type SalaryComponent = {
  pay_item_id?: string;
  short_code?: string;
  name: string;
  item_type: 'EARNING' | 'DEDUCTION' | 'EMPLOYER_CONTRIBUTION' | string;
  formula_type?: string;
  formula_expr?: string;
  /** Effective percentage (or flat value) behind `amount`. Drives the editable
   *  "%" column — null when no percentage produced the row (flat/flexi/balancer). */
  formula_value?: number | string | null;
  amount: number;
  is_in_gross?: boolean;
  is_prorate?: boolean;
  is_flexi?: boolean;
  is_variable?: boolean;
  show_on_payslip?: boolean;
  note?: string | null;
  // Section-placement + statutory metadata (statutory-as-pay-items).
  is_in_ctc?: boolean;
  /** False = above CTC (joining / retention bonus). The balancer does not
   *  reserve for it, so the CTC reconciliation must leave it out too. */
  include_in_ctc?: boolean;
  /** True when the pay-item catalog classifies this head as VARIABLE
   *  (`pay_category='VARIABLE'` or non-recurring). Paired with
   *  `include_in_ctc` it decides where the Salary tab shows the row:
   *    variable + include_in_ctc=false → the above-CTC payout SCHEDULE
   *                                      (its own table, needs a payout month)
   *    variable + include_in_ctc=true  → the "Variable (in CTC)" section —
   *                                      part of the package, so a plain
   *                                      amount with no month
   *    not variable                    → ordinary Earnings */
  is_variable_head?: boolean;
  display_group?: string | null;
  is_statutory?: boolean;
  computed?: boolean;
  /** This row is the employee's own pay item (an ADDITIONAL override), not a
   *  component of their structure. It matters for "Include in Gross": the
   *  engine leaves an off-gross STRUCTURE earning out of gross AND out of net
   *  (net = gross − deductions), but adds every ADDITIONAL row to gross
   *  whatever the flag says. */
  is_additional?: boolean;
  /** Rupees the statutory minimum-wage floor added to this row (Basic only). */
  min_wage_lift?: number | string | null;
};

export type SalaryAssignment = {
  employee_id: string;
  structure_id: string | null;
  structure_name: string | null;
  annual_ctc: number | string | null;
  monthly_ctc: number | string | null;
  /** Package the components commit — the Employees list's Annual CTC figure.
   *  With `ctc_is_derived` it is the only annual figure the employee has;
   *  show it labelled as derived, never as an agreed CTC. */
  monthly_package?: number;
  annual_package?: number;
  ctc_is_derived?: boolean;
  salary_source?: SalarySource | null;
  /** Who wrote the employee's PAY ITEMS — IMPORTED / MANUAL / NONE. Unlike
   *  `salary_source` (which reads STRUCTURE as soon as one is assigned) this
   *  keeps answering that question for a structured employee, so the Salary
   *  tab can hide "reset to structure template" on uploaded amounts. */
  pay_items_source?: SalarySource | null;
  components: SalaryComponent[];
  min_wage_check: {
    state: string | null;
    skill: string | null;
    min_wage_monthly: number | null;
    actual_monthly: number | null;
    is_compliant: boolean;
    deficit: number | null;
    /** False when the entity has opted out of enforcement (report-only). */
    enforced?: boolean;
    /** Rupees the floor actually added to Basic (0 when the structure already cleared it). */
    applied_lift?: number | null;
    /** Component category/short codes that make up the notified wage base. */
    components?: string[];
  } | null;
  /** Per-employee variable pool override (NULL = inherit). */
  variable_pool_annual_override?: number | string | null;
  /** Structure's variable_pool_annual (the template). */
  structure_variable_pool_annual?: number | string | null;
  /** Effective = override > structure. */
  effective_variable_pool_annual?: number | string | null;
  /** Echo of `employees.salary_overrides` for editor convenience. */
  salary_overrides?: SalaryOverride[];
  /** Suggested monthly amounts (by short_code) to pre-fill when HR adds a
   *  state-scheduled statutory item (e.g. LWF_EE/LWF_ER for the work_state). */
  statutory_suggestions?: Record<string, number>;
};

export function useEmployeeSalaryAssignment(employee_id: string | null | undefined) {
  return useQuery({
    queryKey: ['employees', 'salary-assignment', employee_id],
    queryFn: () => api.get<SalaryAssignment>(`/employees/${employee_id}/salary-assignment`),
    enabled: !!employee_id,
  });
}

/**
 * Sweep MM.6 #24 — POST /employees/{id}/salary-assignment writes the
 * structure_id + annual_ctc on the employee AND snapshots the resolved
 * component list (with notes / overrides) into EmployeeSalaryAssignmentLog
 * in one shot. The audit flagged this endpoint as "either dead or missing
 * UI" — existing flows (useAssignCtc + useUpdateSalaryOverrides) take two
 * round-trips and don't capture a single change_note across both.
 *
 * Use this for atomic "assign + customise + record" flows (the assignment
 * wizard, future bulk re-assignment with overrides, etc.).
 */
export type SaveSalaryAssignmentPayload = {
  structure_id: string;
  annual_ctc: number;
  components: Array<{
    pay_item_id?: string | null;
    short_code?: string | null;
    name: string;
    amount: number;
    formula_type?: string | null;
    note?: string | null;
  }>;
  changed_by?: string | null;
  change_note?: string | null;
};

export function useSaveSalaryAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; body: SaveSalaryAssignmentPayload }) =>
      api.post<SalaryAssignment>(`/employees/${vars.id}/salary-assignment`, vars.body),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['employees', 'salary-assignment', vars.id] });
      qc.invalidateQueries({ queryKey: ['employees', 'salary-history', vars.id] });
      qc.invalidateQueries({ queryKey: ['employees', vars.id] });
    },
  });
}

export type SalaryAssignmentLogEntry = {
  id: string;
  employee_id: string;
  structure_id: string;
  structure_name: string;
  annual_ctc: number;
  component_snapshot: SalaryComponent[];
  overrides_applied: SalaryComponent[];
  changed_by: string | null;
  change_note: string | null;
  created_at: string;
};

export function useEmployeeSalaryHistory(employee_id: string | null | undefined) {
  return useQuery({
    queryKey: ['employees', 'salary-history', employee_id],
    queryFn: () => api.get<SalaryAssignmentLogEntry[]>(`/employees/${employee_id}/salary-assignment/log`),
    enabled: !!employee_id,
  });
}

// ── Quick CTC + structure update ────────────────────────────────────────────

export function useAssignCtc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; salary_structure_id?: string | null; annual_ctc?: number | null }) =>
      api.patch<Employee>(`/employees/${vars.id}/ctc`, {
        salary_structure_id: vars.salary_structure_id,
        annual_ctc: vars.annual_ctc,
      }),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['employees'] });
      qc.invalidateQueries({ queryKey: ['employees', 'salary-assignment', vars.id] });
      qc.invalidateQueries({ queryKey: ['employees', 'audit', vars.id] });
    },
  });
}

// ── Per-employee Variable Pay schedule ─────────────────────────────────────

export type VariablePaySchedule = {
  employee_id: string;
  plan_id: string | null;
  plan_name?: string;
  annual_budget: number | null;
  frequency: 'MONTHLY' | 'QUARTERLY' | 'HALF_YEARLY' | 'ANNUAL' | 'ONE_TIME' | null;
  amount_per_payout: number | null;
  payment_mode: string | null;
  tax_handling: string | null;
  formula_type?: string | null;
  formula_config?: Record<string, unknown> | null;
  awards_total: number;
  awards_draft: number;
  awards_approved?: number;
  awards_paid: number;
  awards_created_now?: number;
};

export type VariablePayScheduleIn = {
  annual_budget?: number | null;
  frequency: 'MONTHLY' | 'QUARTERLY' | 'HALF_YEARLY' | 'ANNUAL' | 'ONE_TIME';
  amount_per_payout?: number | null;
  payment_mode?: string;
  tax_handling?: string;
  auto_generate_awards?: boolean;
  formula_type?: string;
  formula_config?: Record<string, unknown> | null;
};

export function useVariablePaySchedule(employee_id: string | null | undefined) {
  return useQuery({
    queryKey: ['employees', 'variable-pay-schedule', employee_id],
    queryFn: () => api.get<VariablePaySchedule>(`/employees/${employee_id}/variable-pay-schedule`),
    enabled: !!employee_id,
  });
}

export function useSaveVariablePaySchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; body: VariablePayScheduleIn }) =>
      api.post<VariablePaySchedule>(`/employees/${vars.id}/variable-pay-schedule`, vars.body),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['employees', 'variable-pay-schedule', vars.id] });
      qc.invalidateQueries({ queryKey: ['employees', 'salary-assignment', vars.id] });
      qc.invalidateQueries({ queryKey: ['employees'] });
      qc.invalidateQueries({ queryKey: ['incentives'] });
    },
  });
}

// ── Variable pay items (scheduled, ABOVE CTC) ──────────────────────────────
//
// The "Variable" section on the Salary tab: joining bonus, retention bonus,
// sign-on, referral — paid in one or more instalments in named payroll months,
// on top of the CTC promise (never reserved by the Special balancer).
//
// The list of selectable heads comes from the pay-item catalog, so an instance
// adds a new bonus type by configuring a pay item, not by a release.

export type VariablePayItemCatalogEntry = {
  id: string;
  name: string;
  short_code: string;
  pay_category: string | null;
  is_taxable: boolean;
  tax_exemption_section: string | null;
  description: string | null;
};

export type VariablePayItem = {
  id: string;
  employee_id: string;
  pay_item_id: string;
  pay_item_name: string | null;
  short_code: string | null;
  /** Number of instalments — how many payroll months may be picked. */
  payout_count: number;
  /** Exactly `payout_count` month labels, e.g. ["Jun 2026", "Dec 2026"]. */
  payout_months: string[];
  amount_per_payout: number;
  total_amount: number;
  payment_mode: string;
  tax_handling: string;
  is_above_ctc: boolean;
  notes: string | null;
  is_active: boolean;
};

export type VariablePayItemIn = {
  pay_item_id: string;
  payout_count: number;
  payout_months: string[];
  amount_per_payout: number;
  payment_mode: string;
  tax_handling: string;
  is_above_ctc?: boolean;
  notes?: string | null;
  is_active?: boolean;
};

/** Pay items eligible as a variable payout. `itemType='EARNING'` (default) is
 *  the above-CTC bonus catalog (pay_category=VARIABLE or non-recurring
 *  earnings); `'DEDUCTION'` is the non-statutory deduction heads used by the
 *  Variable-Payout recovery dropdown. */
export function useVariablePayItemCatalog(itemType: 'EARNING' | 'DEDUCTION' = 'EARNING') {
  return useQuery({
    queryKey: ['pay-items', 'variable-catalog', itemType],
    queryFn: () => api.get<VariablePayItemCatalogEntry[]>(
      '/pay-items/variable-catalog', { item_type: itemType },
    ),
  });
}

export function useVariablePayItems(employee_id: string | null | undefined) {
  return useQuery({
    queryKey: ['employees', 'variable-pay-items', employee_id],
    queryFn: () => api.get<VariablePayItem[]>(`/employees/${employee_id}/variable-pay-items`),
    enabled: !!employee_id,
  });
}

export type PayoutMonthStatus = 'OPEN' | 'LOCKED' | 'PAID';

export type PayoutMonth = {
  /** Month label, e.g. "Jun 2026". */
  label: string;
  /** OPEN = selectable; LOCKED/PAID = shown disabled (payroll already settled). */
  status: PayoutMonthStatus;
};

export type PayoutMonths = {
  /** The current financial year's months (Apr→Mar), each with a settled status.
   *  Locked/paid months are listed (not hidden) so the picker can disable them. */
  months: PayoutMonth[];
  joining_month: string | null;
};

/** Selectable payout months for the above-CTC variable scheduler — the current
 *  financial year from the employee's joining month, each flagged OPEN /
 *  LOCKED / PAID so settled months render disabled rather than disappearing. */
export function usePayoutMonths(employee_id: string | null | undefined) {
  return useQuery({
    queryKey: ['employees', 'payout-months', employee_id],
    queryFn: () => api.get<PayoutMonths>(`/employees/${employee_id}/payout-months`),
    enabled: !!employee_id,
  });
}

/** Invalidate the employee's variable-pay-item list after any write. The
 *  salary-assignment query is deliberately NOT invalidated: these rows sit
 *  above CTC and change nothing in the monthly breakdown. */
function useVariablePayItemMutation<TVars>(
  fn: (vars: TVars & { id: string }) => Promise<unknown>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (_d, vars) =>
      qc.invalidateQueries({ queryKey: ['employees', 'variable-pay-items', vars.id] }),
  });
}

export function useCreateVariablePayItem() {
  return useVariablePayItemMutation<{ body: VariablePayItemIn }>(
    (vars) => api.post<VariablePayItem>(`/employees/${vars.id}/variable-pay-items`, vars.body),
  );
}

export function useUpdateVariablePayItem() {
  return useVariablePayItemMutation<{ itemId: string; body: VariablePayItemIn }>(
    (vars) => api.put<VariablePayItem>(
      `/employees/${vars.id}/variable-pay-items/${vars.itemId}`, vars.body),
  );
}

export function useDeleteVariablePayItem() {
  return useVariablePayItemMutation<{ itemId: string }>(
    (vars) => api.delete<void>(`/employees/${vars.id}/variable-pay-items/${vars.itemId}`),
  );
}

/** Replace the employee's salary_overrides list. Used by the inline editor
 *  on the Salary Structure tab so HR can add/edit per-employee adjustments
 *  (Site Allowance, Expat top-up, override Basic, …) without going through
 *  the full Employee Edit drawer. */
export function useUpdateSalaryOverrides() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      id: string;
      salary_overrides: SalaryOverride[];
      annual_ctc?: number | null;
      variable_pool_annual_override?: number | null;
    }) => {
      const body: Record<string, unknown> = { salary_overrides: vars.salary_overrides };
      if (vars.annual_ctc !== undefined) body.annual_ctc = vars.annual_ctc;
      if (vars.variable_pool_annual_override !== undefined) {
        body.variable_pool_annual_override = vars.variable_pool_annual_override;
      }
      return api.patch<Employee>(`/employees/${vars.id}/salary-overrides`, body);
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['employees'] });
      qc.invalidateQueries({ queryKey: ['employees', 'salary-assignment', vars.id] });
      qc.invalidateQueries({ queryKey: ['employees', 'audit', vars.id] });
    },
  });
}
